import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Button,
  Typography,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";
import { Archive, Send, Edit2 } from "lucide-react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const RESOURCE_LABELS = {
  candidates: "candidate",
  recruiters: "client",
  external_profiles: "profile",
  companies: "company",
};

const C = {
  primary: "hsl(150 38% 17%)",
  primaryFg: "hsl(80 97% 72%)",
  text: "hsl(150 30% 12%)",
  muted: "hsl(150 10% 46%)",
  subtle: "hsl(150 10% 60%)",
  border: "hsl(140 8% 88%)",
  surface: "hsl(140 10% 95%)",
  rowHover: "hsla(140, 8%, 90%, 0.4)",
};

const authorAvatarUser = (name) => {
  const s = String(name || "?");
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return { id: Math.abs(hash), full_name: name };
};

const formatRelative = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
};

const formatFull = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const NoteRow = ({ note, onArchive, onEdit, canEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);

  const handleSave = () => {
    if (editContent.trim() && editContent !== note.content) {
      onEdit(note.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(note.content);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        px: 2,
        py: 1.5,
        transition: "background-color 0.12s",
        "&:hover": { backgroundColor: C.rowHover },
        "&:hover .action-btn": { opacity: 1 },
      }}
    >
      <UserAvatar user={authorAvatarUser(note.author)} size={28} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: C.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {note.author || "Unknown"}
          </Typography>
          <Tooltip title={formatFull(note.created_at)} arrow placement="top">
            <Typography
              sx={{ fontSize: 11, color: C.subtle, fontFamily: "Segoe UI Regular, sans-serif, system-ui", flexShrink: 0 }}
            >
              {formatRelative(note.created_at)}
            </Typography>
          </Tooltip>
        </Stack>
        {isEditing ? (
          <Box sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              multiline
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: 13,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  borderRadius: "8px",
                },
              }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button size="small" variant="contained" onClick={handleSave} sx={{ textTransform: "none", fontSize: 12 }}>
                Save
              </Button>
              <Button size="small" onClick={handleCancel} sx={{ textTransform: "none", fontSize: 12 }}>
                Cancel
              </Button>
            </Stack>
          </Box>
        ) : (
          <Typography
            sx={{
              mt: 0.25,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 13,
              color: C.text,
              lineHeight: 1.5,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            }}
          >
            {note.content}
          </Typography>
        )}
      </Box>
      {!isEditing && (
        <Stack direction="row" spacing={0.5} sx={{ alignSelf: "flex-start" }}>
          {canEdit && (
            <Tooltip title="Edit note" arrow placement="left">
              <IconButton
                className="action-btn"
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{
                  p: 0.5,
                  borderRadius: "6px",
                  color: C.muted,
                  opacity: 0,
                  transition: "opacity 0.15s ease",
                  "&:hover": { backgroundColor: C.surface, color: C.text },
                }}
              >
                <Edit2 size={13} />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Archive note" arrow placement="left">
              <IconButton
                className="action-btn"
                size="small"
                onClick={() => onArchive(note.id)}
                sx={{
                  p: 0.5,
                  borderRadius: "6px",
                  color: C.muted,
                  opacity: 0,
                  transition: "opacity 0.15s ease",
                  "&:hover": { backgroundColor: C.surface, color: C.text },
                }}
              >
                <Archive size={13} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
};

const FeedSkeleton = () => (
  <Box>
    {[0, 1, 2].map((i) => (
      <Box key={i} sx={{ display: "flex", gap: 1.5, px: 2, py: 1.5 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="40%" height={14} />
          <Skeleton width="92%" />
          <Skeleton width="70%" />
        </Box>
      </Box>
    ))}
  </Box>
);

const EmptyFeed = () => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      py: 4,
      px: 2,
      color: C.muted,
    }}
  >
    <NotesIcon sx={{ fontSize: 28, opacity: 0.4 }} />
    <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
      No notes yet — start the conversation
    </Typography>
  </Box>
);

const Composer = ({ value, onChange, onSubmit, disabled, saving, placeholder }) => (
  <Box
    sx={{
      borderTop: `1px solid ${C.border}`,
      backgroundColor: "#fff",
      p: 1.25,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: 1,
        backgroundColor: C.surface,
        borderRadius: "12px",
        border: `1px solid ${C.border}`,
        px: 1.25,
        py: 0.75,
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:focus-within": {
          borderColor: C.primary,
          boxShadow: "0 0 0 2px hsla(150, 38%, 17%, 0.12)",
        },
      }}
    >
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        fullWidth
        variant="standard"
        multiline
        maxRows={6}
        InputProps={{ disableUnderline: true }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !disabled) {
            e.preventDefault();
            onSubmit();
          }
        }}
        sx={{
          flex: 1,
          "& .MuiInputBase-input": {
            fontSize: 13,
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            color: C.text,
            py: 0.75,
          },
          "& textarea::placeholder": {
            color: C.muted,
            opacity: 1,
          },
        }}
      />
      <Tooltip title={saving ? "Sending…" : "Send (⌘/Ctrl + Enter)"} arrow placement="top">
        <span>
          <IconButton
            onClick={onSubmit}
            disabled={disabled}
            sx={{
              backgroundColor: C.primary,
              color: C.primaryFg,
              borderRadius: "10px",
              width: 40,
              height: 40,
              alignSelf: "flex-end",
              transition: "opacity 0.15s, transform 0.15s",
              "&:hover": {
                backgroundColor: C.primary,
                opacity: 0.92,
                transform: "translateY(-1px)",
              },
              "&.Mui-disabled": {
                backgroundColor: C.primary,
                color: C.primaryFg,
                opacity: 0.35,
              },
            }}
          >
            <Send size={18} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  </Box>
);

const NotesPanel = ({
  resource,
  recordId,
  placeholder,
  onNoteAdded,
  onNoteArchived,
}) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();

  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const feedRef = useRef(null);

  const baseUrl = `/internal/${resource}/${recordId}/notes`;
  const inputPlaceholder =
    placeholder || `Note about this ${RESOURCE_LABELS[resource] || "record"}…`;

  useEffect(() => {
    if (!resource || recordId == null) return;

    setLoading(true);
    sh.get(baseUrl)
      .then((res) => setNotes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [resource, recordId]);

  const addNote = () => {
    const content = draft.trim();
    if (!content || recordId == null) return;

    setSaving(true);
    sh.post(baseUrl, { note: { content }, authenticity_token })
      .then((res) => {
        setNotes((prev) => [res.data, ...prev]);
        setDraft("");
        onNoteAdded?.(recordId);
        if (feedRef.current) feedRef.current.scrollTop = 0;
      })
      .finally(() => setSaving(false));
  };

  const archiveNote = useCallback((noteId) => {
    setArchiveTargetId(noteId);
  }, []);

  const editNote = useCallback((noteId, newContent) => {
    sh.patch(`${baseUrl}/${noteId}`, {
      authenticity_token,
      note: { content: newContent }
    }).then(() => {
      setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, content: newContent } : n));
    }).catch((err) => {
      if (err?.response?.status === 403) {
        enqueueSnackbar("You can only edit notes you created", { variant: "error" });
      } else {
        enqueueSnackbar("Failed to edit note", { variant: "error" });
      }
    });
  }, [baseUrl, authenticity_token, enqueueSnackbar]);

  const confirmArchive = useCallback(() => {
    const noteId = archiveTargetId;
    if (noteId == null) return;

    sh.delete(`${baseUrl}/${noteId}`, { data: { authenticity_token } }).then(() => {
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      onNoteArchived?.(recordId);
    }).catch((err) => {
      if (err?.response?.status === 403) {
        enqueueSnackbar("You can only delete notes you created", { variant: "error" });
      } else {
        enqueueSnackbar("Failed to delete note", { variant: "error" });
      }
    });
    setArchiveTargetId(null);
  }, [archiveTargetId, baseUrl, recordId, authenticity_token, onNoteArchived, enqueueSnackbar]);

  const submitDisabled = saving || draft.trim().length === 0 || recordId == null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: "0.75rem",
        border: `1px solid ${C.border}`,
        overflow: "hidden",
        height: "100%",
        maxHeight: "calc(100vh - 120px)",
        minHeight: 360,
      }}
    >
      {/* Slim header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: C.muted,
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Notes
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: C.subtle,
            }}
          >
            · {notes.length}
          </Typography>
        </Stack>
      </Box>

      {/* Feed */}
      <Box
        ref={feedRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "hsl(140 8% 82%)",
            borderRadius: 3,
            "&:hover": { background: C.muted },
          },
        }}
      >
        {loading ? (
          <FeedSkeleton />
        ) : notes.length === 0 ? (
          <EmptyFeed />
        ) : (
          <Box sx={{ py: 0.5 }}>
            {notes.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                onArchive={archiveNote}
                onEdit={editNote}
                canEdit={!!n.editable}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Composer pinned at bottom */}
      <Composer
        value={draft}
        onChange={setDraft}
        onSubmit={addNote}
        disabled={submitDisabled}
        saving={saving}
        placeholder={inputPlaceholder}
      />

      <ConfirmDialog
        open={archiveTargetId !== null}
        onClose={() => setArchiveTargetId(null)}
        onConfirm={confirmArchive}
        title="Archive this note?"
        description="The note will be removed from the list. This action cannot be undone from here."
        confirmLabel="Archive"
        destructive
      />
    </Box>
  );
};

export default NotesPanel;
